/**
 * Shared types, formatters, and presentational sub-components for the TSM
 * Ranking Reports.
 *
 * Used by both:
 *  - `TsmRankingReport.tsx`         (company-wide single leaderboard)
 *  - `TsmGroupRankingReport.tsx`    (split into Domestic / Import / Express /
 *                                   Unassigned, each ranked independently)
 *
 * Keep this file small and presentational — no data fetching, no top-level
 * report layout. Top-level layout (header, methodology footer) lives in each
 * report's renderer.
 */

import React, { useMemo, useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';

// ──────────────────────────────────────────────────────────────────────────
// Types (mirror the executor payload)
// ──────────────────────────────────────────────────────────────────────────

export type MetricKey =
  | 'openROCount'
  | 'oldestRoAgeDays'
  | 'mtdCpScGross'
  | 'avgHoursCp'
  | 'mtdTotalGross'
  | 'cpElr';

export interface TsmRankedRow {
  advisorId: string;
  advisorName: string;
  locationId: number;
  locationName: string;
  closedROCount: number;
  openROCount: number;
  oldestRoAgeDays: number;
  oldestOpenRODate: string | null;
  mtdCpScGross: number;
  avgHoursCp: number;
  mtdTotalGross: number;
  cpElr: number;
  rankOpenRos: number;
  rankOldestRo: number;
  rankCpScGross: number;
  rankAvgHoursCp: number;
  rankTotalGross: number;
  rankCpElr: number;
  totalScore: number;
}

export interface MultiLocationExcludedAdvisor {
  advisorId: string;
  advisorName: string;
  locations: string[];
}

export interface MetricLeader {
  metric: MetricKey;
  label: string;
  winners: Array<{
    advisorId: string;
    advisorName: string;
    locationName: string;
    value: number;
  }>;
}

export interface TsmLocationRow {
  locationId: number;
  locationName: string;
  advisorCount: number;
  closedROCount: number;
  openROCount: number;
  oldestRoAgeDays: number;
  oldestOpenRODate: string | null;
  avgHoursCp: number;
  cpElr: number;
  controllableExpensePct: number;
  variancePct: number;
  mtdCpScGross: number;
  mtdTotalGross: number;
  rankOpenRos: number;
  rankOldestRo: number;
  rankAvgHoursCp: number;
  rankCpElr: number;
  rankControllableExpense: number;
  rankVariancePct: number;
  totalScore: number;
}

// ──────────────────────────────────────────────────────────────────────────
// Formatters (exported — Modal Excel exports use them too)
// ──────────────────────────────────────────────────────────────────────────

export const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});
export const moneyDetailFmt = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});
export const numFmt = new Intl.NumberFormat('en-US');
export const fmtPct = (v: number) => `${v.toFixed(1)}%`;
export const fmtHours = (v: number) => `${v.toFixed(2)} hrs`;
// Closed/open RO counts, e.g. "100/33" — same pairing as the Service Advisor
// Performance card on the Forecast page (closed first, open second).
export const fmtClosedOpen = (closed: number, open: number) =>
  `${numFmt.format(closed)}/${numFmt.format(open)}`;
export const fmtAge = (days: number, date: string | null) => {
  if (!date || days === 0) return '—';
  return `${numFmt.format(days)}d`;
};
export const fmtAsOf = (iso: string | null) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric',
      hour: 'numeric', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};
export const fmtPctSigned = (v: number): string => {
  if (v === 0) return '0.0%';
  const sign = v > 0 ? '+' : '';
  return `${sign}${v.toFixed(1)}%`;
};
export const varianceTextClass = (v: number): string => {
  if (v >= 0) return 'text-green-700';
  if (v > -5) return 'text-amber-700';
  return 'text-rose-700';
};

export function formatMetricValue(metric: MetricKey, value: number): string {
  switch (metric) {
    case 'openROCount': return numFmt.format(value);
    case 'oldestRoAgeDays': return value === 0 ? '—' : `${numFmt.format(value)}d`;
    case 'mtdCpScGross': return currencyFmt.format(value);
    case 'avgHoursCp': return fmtHours(value);
    case 'mtdTotalGross': return currencyFmt.format(value);
    case 'cpElr': return moneyDetailFmt.format(value);
  }
}

// Rank chip color — top quartile green, bottom quartile rose, middle gray.
export function rankChipClass(rank: number, total: number): string {
  if (total <= 0) return 'bg-gray-100 text-gray-700';
  const q1 = Math.max(1, Math.ceil(total * 0.25));
  const q4 = Math.floor(total * 0.75);
  if (rank <= q1) return 'bg-green-50 text-green-700 ring-1 ring-green-200';
  if (rank > q4) return 'bg-rose-50 text-rose-700 ring-1 ring-rose-200';
  return 'bg-gray-100 text-gray-700 ring-1 ring-gray-200';
}

// ──────────────────────────────────────────────────────────────────────────
// Presentational primitives
// ──────────────────────────────────────────────────────────────────────────

export const RankCell: React.FC<{ rank: number; total: number }> = ({ rank, total }) => (
  <span
    className={`inline-flex items-center justify-center min-w-[1.5rem] px-1.5 py-0.5 rounded text-xs font-bold tabular-nums ${rankChipClass(
      rank,
      total,
    )}`}
  >
    {rank}
  </span>
);

const SortArrow: React.FC<{ active: boolean; direction: 'asc' | 'desc' }> = ({ active, direction }) => (
  <span className={`ml-1 text-[10px] ${active ? 'text-gray-900' : 'text-gray-300'}`}>
    {active ? (direction === 'asc' ? '▲' : '▼') : '▾'}
  </span>
);

interface SortableHeaderProps<K extends string> {
  label: string;
  sortKey: K;
  activeKey: K;
  activeDir: 'asc' | 'desc';
  onClick: (key: K) => void;
  align?: 'left' | 'right';
  emphasize?: boolean;
}
export function SortableHeader<K extends string>({
  label, sortKey, activeKey, activeDir, onClick, align = 'left', emphasize,
}: SortableHeaderProps<K>) {
  const active = activeKey === sortKey;
  return (
    <th
      onClick={() => onClick(sortKey)}
      className={`px-2 py-2 ${align === 'right' ? 'text-right' : 'text-left'} font-semibold uppercase tracking-wide cursor-pointer select-none hover:bg-gray-100 transition-colors print:cursor-default print:hover:bg-transparent ${
        emphasize ? 'text-gray-800 bg-gray-100' : 'text-gray-700'
      } ${active ? 'bg-blue-50 print:bg-transparent' : ''}`}
    >
      <span className="inline-flex items-center">
        {label}
        <SortArrow active={active} direction={activeDir} />
      </span>
    </th>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Metric Leaders strip
// ──────────────────────────────────────────────────────────────────────────

export const MetricLeaders: React.FC<{ leaders: MetricLeader[] }> = ({ leaders }) => (
  <div className="mb-8 print:mb-6 print:break-inside-avoid">
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2">
      Metric Leaders
    </div>
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {leaders.map((leader) => (
        <div
          key={leader.metric}
          className="bg-white border border-gray-200 rounded-lg p-3 print:p-2"
        >
          <div className="flex items-center gap-1.5 text-xs font-medium text-gray-500 uppercase tracking-wide">
            <StarIcon className="h-3.5 w-3.5 text-green-600" />
            {leader.label}
          </div>
          {leader.winners.length === 0 ? (
            <div className="mt-1 text-sm text-gray-400">—</div>
          ) : (
            <div className="mt-1">
              <div className="text-sm font-semibold text-gray-900 leading-tight">
                {leader.winners.map((w) => w.advisorName).join(', ')}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">
                {leader.winners[0].locationName}
                {leader.winners.length > 1 && ` · +${leader.winners.length - 1} tied`}
              </div>
              <div className="text-sm font-bold text-green-700 mt-1 tabular-nums">
                {formatMetricValue(leader.metric, leader.winners[0].value)}
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ──────────────────────────────────────────────────────────────────────────
// Ranked TSM table (used by both reports — group report renders one per group)
// ──────────────────────────────────────────────────────────────────────────

type SortKey =
  | 'totalScore'
  | 'advisorName'
  | 'locationName'
  | 'openROCount'
  | 'oldestRoAgeDays'
  | 'mtdCpScGross'
  | 'avgHoursCp'
  | 'mtdTotalGross'
  | 'cpElr';

const SORT_DEFAULT_DIR: Record<SortKey, 'asc' | 'desc'> = {
  totalScore: 'asc',
  advisorName: 'asc',
  locationName: 'asc',
  openROCount: 'asc',
  oldestRoAgeDays: 'asc',
  mtdCpScGross: 'desc',
  avgHoursCp: 'desc',
  mtdTotalGross: 'desc',
  cpElr: 'desc',
};

export const RankedTable: React.FC<{ rows: TsmRankedRow[] }> = ({ rows }) => {
  const total = rows.length;
  const [sortKey, setSortKey] = useState<SortKey>('totalScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(SORT_DEFAULT_DIR[key]);
    }
  };

  const sortedRows = useMemo(() => {
    const out = [...rows];
    out.sort((a, b) => {
      const av = a[sortKey] as string | number;
      const bv = b[sortKey] as string | number;
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv);
      } else {
        cmp = (av as number) - (bv as number);
      }
      if (cmp === 0) {
        cmp = a.totalScore - b.totalScore;
        if (cmp === 0) cmp = a.advisorName.localeCompare(b.advisorName);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [rows, sortKey, sortDir]);

  if (rows.length === 0) {
    return (
      <div className="text-xs text-gray-500 italic px-2 py-4 border border-gray-100 rounded bg-gray-50">
        No advisors in this group yet.
      </div>
    );
  }

  return (
    <div className="overflow-x-auto print:overflow-visible print:break-inside-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b-2 border-gray-300">
            <th className="px-2 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide">#</th>
            <SortableHeader label="Advisor" sortKey="advisorName" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} />
            <SortableHeader label="Location" sortKey="locationName" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} />
            <SortableHeader label="Closed/Open ROs" sortKey="openROCount" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="Oldest RO" sortKey="oldestRoAgeDays" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="CP+SC Gross" sortKey="mtdCpScGross" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="Avg Hrs/RO (CP)" sortKey="avgHoursCp" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="Total Labor Gross" sortKey="mtdTotalGross" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="CP ELR" sortKey="cpElr" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader label="Score" sortKey="totalScore" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" emphasize />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => (
            <tr
              key={row.advisorId}
              className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 border-b border-gray-200`}
            >
              <td className="px-2 py-1.5 font-bold tabular-nums text-gray-900">{i + 1}</td>
              <td className="px-2 py-1.5 font-medium text-gray-900 whitespace-nowrap">{row.advisorName}</td>
              <td className="px-2 py-1.5 text-gray-600 whitespace-nowrap">{row.locationName}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{fmtClosedOpen(row.closedROCount, row.openROCount)}</span>
                <RankCell rank={row.rankOpenRos} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{fmtAge(row.oldestRoAgeDays, row.oldestOpenRODate)}</span>
                <RankCell rank={row.rankOldestRo} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{currencyFmt.format(row.mtdCpScGross)}</span>
                <RankCell rank={row.rankCpScGross} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{fmtHours(row.avgHoursCp)}</span>
                <RankCell rank={row.rankAvgHoursCp} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{currencyFmt.format(row.mtdTotalGross)}</span>
                <RankCell rank={row.rankTotalGross} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-700">
                <span className="mr-1.5">{moneyDetailFmt.format(row.cpElr)}</span>
                <RankCell rank={row.rankCpElr} total={total} />
              </td>
              <td className="px-2 py-1.5 text-right font-bold tabular-nums text-gray-900 bg-gray-50">
                {row.totalScore}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ──────────────────────────────────────────────────────────────────────────
// Per-location aggregate table (reused unchanged by group report)
// ──────────────────────────────────────────────────────────────────────────

interface LocationAggregateTableProps {
  locationRows: TsmLocationRow[];
  tsmRowsByLocation: Map<number, TsmRankedRow[]>;
  tsmTotal: number;
}

type LocSortKey =
  | 'totalScore'
  | 'locationName'
  | 'advisorCount'
  | 'openROCount'
  | 'oldestRoAgeDays'
  | 'avgHoursCp'
  | 'cpElr'
  | 'controllableExpensePct'
  | 'variancePct';

const LOC_SORT_DEFAULT_DIR: Record<LocSortKey, 'asc' | 'desc'> = {
  totalScore: 'asc',
  locationName: 'asc',
  advisorCount: 'desc',
  openROCount: 'asc',
  oldestRoAgeDays: 'asc',
  avgHoursCp: 'desc',
  cpElr: 'desc',
  controllableExpensePct: 'asc',
  variancePct: 'desc',
};

export const LocationAggregateTable: React.FC<LocationAggregateTableProps> = ({
  locationRows, tsmRowsByLocation, tsmTotal,
}) => {
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [sortKey, setSortKey] = useState<LocSortKey>('totalScore');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const total = locationRows.length;
  const toggle = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSort = (key: LocSortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(LOC_SORT_DEFAULT_DIR[key]);
    }
  };

  const sortedRows = useMemo(() => {
    const out = [...locationRows];
    out.sort((a, b) => {
      const av = a[sortKey] as string | number;
      const bv = b[sortKey] as string | number;
      let cmp: number;
      if (typeof av === 'string' && typeof bv === 'string') {
        cmp = av.localeCompare(bv);
      } else {
        cmp = (av as number) - (bv as number);
      }
      if (cmp === 0) {
        cmp = a.totalScore - b.totalScore;
        if (cmp === 0) cmp = a.locationName.localeCompare(b.locationName);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return out;
  }, [locationRows, sortKey, sortDir]);

  return (
    <div className="overflow-x-auto print:overflow-visible print:break-inside-auto">
      <table className="min-w-full text-xs border-collapse">
        <thead>
          <tr className="bg-gray-50 border-b-2 border-gray-300">
            <th className="px-2 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide w-6"></th>
            <th className="px-2 py-2 text-left font-semibold text-gray-700 uppercase tracking-wide">#</th>
            <SortableHeader<LocSortKey> label="Location" sortKey="locationName" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} />
            <SortableHeader<LocSortKey> label="# TSMs" sortKey="advisorCount" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Closed/Open ROs" sortKey="openROCount" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Oldest RO" sortKey="oldestRoAgeDays" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Avg Hrs/RO (CP+SC)" sortKey="avgHoursCp" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="CP ELR" sortKey="cpElr" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Ctrl Exp %" sortKey="controllableExpensePct" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Trend vs Proj %" sortKey="variancePct" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" />
            <SortableHeader<LocSortKey> label="Score" sortKey="totalScore" activeKey={sortKey} activeDir={sortDir} onClick={handleSort} align="right" emphasize />
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row, i) => {
            const isOpen = expanded.has(row.locationId);
            const tsms = tsmRowsByLocation.get(row.locationId) ?? [];
            return (
              <React.Fragment key={row.locationId}>
                <tr
                  onClick={() => toggle(row.locationId)}
                  className={`${i % 2 === 0 ? 'bg-white' : 'bg-gray-50'} hover:bg-blue-50 border-b border-gray-200 cursor-pointer select-none print:cursor-default`}
                >
                  <td className="px-2 py-2 text-gray-500 text-sm">{isOpen ? '▾' : '▸'}</td>
                  <td className="px-2 py-2 font-bold tabular-nums text-gray-900">{i + 1}</td>
                  <td className="px-2 py-2 font-semibold text-gray-900 whitespace-nowrap">{row.locationName}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">{row.advisorCount}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    <span className="mr-1.5">{fmtClosedOpen(row.closedROCount, row.openROCount)}</span>
                    <RankCell rank={row.rankOpenRos} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    <span className="mr-1.5">{fmtAge(row.oldestRoAgeDays, row.oldestOpenRODate)}</span>
                    <RankCell rank={row.rankOldestRo} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    <span className="mr-1.5">{fmtHours(row.avgHoursCp)}</span>
                    <RankCell rank={row.rankAvgHoursCp} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    <span className="mr-1.5">{moneyDetailFmt.format(row.cpElr)}</span>
                    <RankCell rank={row.rankCpElr} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums text-gray-700">
                    <span className="mr-1.5">{fmtPct(row.controllableExpensePct)}</span>
                    <RankCell rank={row.rankControllableExpense} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right tabular-nums">
                    <span className={`mr-1.5 font-medium ${varianceTextClass(row.variancePct)}`}>
                      {fmtPctSigned(row.variancePct)}
                    </span>
                    <RankCell rank={row.rankVariancePct} total={total} />
                  </td>
                  <td className="px-2 py-2 text-right font-bold tabular-nums text-gray-900 bg-gray-50">
                    {row.totalScore}
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={11} className="px-2 pb-4 pt-1 bg-blue-50/30">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-600 mb-1">
                        {row.locationName} — {tsms.length} ranked TSM{tsms.length === 1 ? '' : 's'}
                      </div>
                      {tsms.length === 0 ? (
                        <div className="text-xs text-gray-500 italic px-2 py-2">
                          No advisors at this location passed the ranking filters this month.
                        </div>
                      ) : (
                        <table className="min-w-full text-xs border-collapse">
                          <thead>
                            <tr className="bg-white border-b border-gray-300">
                              <th className="px-2 py-1.5 text-left font-medium text-gray-600">Advisor</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">Closed/Open ROs</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">Oldest RO</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">CP+SC Gross</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">Avg Hrs/RO (CP)</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">Total Labor Gross</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-600">CP ELR</th>
                              <th className="px-2 py-1.5 text-right font-medium text-gray-700">Score</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tsms.map((adv) => (
                              <tr key={adv.advisorId} className="border-b border-gray-200">
                                <td className="px-2 py-1 text-gray-900 whitespace-nowrap">{adv.advisorName}</td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{fmtClosedOpen(adv.closedROCount, adv.openROCount)}</span>
                                  <RankCell rank={adv.rankOpenRos} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{fmtAge(adv.oldestRoAgeDays, adv.oldestOpenRODate)}</span>
                                  <RankCell rank={adv.rankOldestRo} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{currencyFmt.format(adv.mtdCpScGross)}</span>
                                  <RankCell rank={adv.rankCpScGross} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{fmtHours(adv.avgHoursCp)}</span>
                                  <RankCell rank={adv.rankAvgHoursCp} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{currencyFmt.format(adv.mtdTotalGross)}</span>
                                  <RankCell rank={adv.rankTotalGross} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right tabular-nums text-gray-700">
                                  <span className="mr-1">{moneyDetailFmt.format(adv.cpElr)}</span>
                                  <RankCell rank={adv.rankCpElr} total={tsmTotal} />
                                </td>
                                <td className="px-2 py-1 text-right font-bold tabular-nums text-gray-900">{adv.totalScore}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
